import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import {
  getInventoryMovements,
  getInventoryItems,
  getOrderConsumptions,
  retryOrderConsumption,
} from '../services/api';
import { normalizeMovementsResponse } from '../utils/movements';
import { describeConsumptionError } from '../utils/consumptionIssues';
import './OrderConsumptionBlock.css';

/**
 * Consumo de inventario de un pedido (B6 / RES-34).
 *
 * Muestra qué insumos descontó el pedido, en qué local y con qué estado. Los movimientos salen
 * de `GET /inventory/movements?refId={orderId}`, que resuelve por el índice de referencias, así
 * que sirve igual para un pedido de otro día.
 *
 * El estado sale del marcador de consumo (`GET /inventory/consumptions?orderId=`), no de los
 * movimientos: `partial` no se puede deducir de ellos, porque lo que no se pudo descontar no
 * existe como movimiento. Antes el bloque derivaba el estado de los movimientos y un pedido que
 * figuraba en "consumos con problemas" mostraba igual el badge Aplicado en verde (RES-59).
 *
 * Si la organización no tiene inventario el endpoint responde error y el bloque simplemente no
 * se muestra: no tiene sentido ocupar lugar en el detalle del pedido.
 *
 * @param {Object} props - Props
 * @param {string} props.orderId - Pedido
 * @param {Array<Object>} [props.inventoryItems] - Catálogo ya cargado, para no volver a pedirlo
 * @returns {JSX.Element|null} Bloque
 */
function OrderConsumptionBlock({ orderId, inventoryItems }) {
  const { t } = useTranslation();
  const [movements, setMovements] = useState([]);
  const [consumption, setConsumption] = useState(null);
  const [items, setItems] = useState(inventoryItems || []);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getInventoryMovements({ refId: orderId, limit: 100 });
      setMovements(normalizeMovementsResponse(response).items);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
    // El marcador es best-effort: si no se puede leer, el bloque sigue mostrando los movimientos
    try {
      const data = await getOrderConsumptions({ orderId });
      setConsumption(data?.items?.[0] || null);
    } catch {
      setConsumption(null);
    }
  }, [orderId]);

  const handleRetry = async () => {
    setRetrying(true);
    setNotice('');
    try {
      const result = await retryOrderConsumption(orderId);
      setNotice(
        result?.status === 'applied'
          ? t('inventory.consumptionIssues.retryFixed')
          : t('inventory.consumptionIssues.retryStillPartial')
      );
      await load();
    } catch (error) {
      setNotice(error?.response?.data?.message || t('inventory.consumptionIssues.retryError'));
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (inventoryItems && inventoryItems.length > 0) return;
    let cancelled = false;
    // RES-70: acá solo resolvemos nombres para movimientos YA ocurridos, no ofrecemos el insumo
    // para elegir — includeInactive para que un insumo desactivado después de la venta no
    // aparezca como UUID crudo.
    getInventoryItems({ includeInactive: true })
      .then((data) => {
        if (!cancelled) setItems(data.items || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [inventoryItems]);

  if (failed) return null;

  const nameOf = (itemId) => items.find((i) => i.itemId === itemId)?.name || itemId;
  const unitOf = (itemId) => items.find((i) => i.itemId === itemId)?.unit || '';

  // El pedido puede tener la venta y, si se canceló, su reposición
  const reversed = movements.some((m) => m.Type === 'SALE_REVERSAL');
  // El marcador manda: es el único que sabe que el consumo quedó incompleto (RES-59)
  const status = consumption?.status || (reversed ? 'reversed' : 'applied');
  const errors = status === 'partial' ? consumption?.errors || [] : [];
  const restaurantId = movements[0]?.RestaurantId || consumption?.restaurantId;

  return (
    <div className="order-consumption">
      <h4>{t('orders.consumption.title')}</h4>

      {loading && <p className="text-muted small">{t('common.loading')}</p>}

      {!loading && movements.length === 0 && !consumption && (
        <p className="text-muted small">{t('orders.consumption.empty')}</p>
      )}

      {!loading && (movements.length > 0 || consumption) && (
        <>
          <div className="order-consumption-meta">
            <span className={`order-consumption-status order-consumption-status--${status}`}>
              {t(`orders.consumption.status.${status}`)}
            </span>
            {restaurantId && (
              <span className="text-muted small">
                {t('orders.consumption.restaurant')}: {restaurantId}
              </span>
            )}
            {status === 'partial' && (
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleRetry}
                disabled={retrying}
              >
                <RefreshCw size={14} aria-hidden="true" />
                {retrying
                  ? t('inventory.consumptionIssues.retrying')
                  : t('inventory.consumptionIssues.retry')}
              </button>
            )}
          </div>

          {errors.length > 0 && (
            <ul className="order-consumption-errors">
              {errors.map((error, index) => (
                <li key={index}>{describeConsumptionError(error, t)}</li>
              ))}
            </ul>
          )}

          {notice && (
            <p className="order-consumption-notice" role="status">
              {notice}
            </p>
          )}

          <ul className="order-consumption-list">
            {movements.map((movement) => (
              <li key={`${movement.PK}|${movement.SK}`}>
                <span className="order-consumption-item">{nameOf(movement.ItemId)}</span>
                <span
                  className={
                    movement.Quantity > 0
                      ? 'order-consumption-qty text-green'
                      : 'order-consumption-qty text-red'
                  }
                >
                  {movement.Quantity > 0 ? '+' : ''}
                  {movement.Quantity} {unitOf(movement.ItemId)}
                </span>
              </li>
            ))}
          </ul>

          <Link to={`/inventory/movements?refId=${orderId}`} className="order-consumption-link">
            {t('orders.consumption.seeMovements')}
          </Link>
        </>
      )}
    </div>
  );
}

export default OrderConsumptionBlock;
